import type { HelmInspectQuery } from '@features/fields/api/useHelmInspect'
import { useMemo } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor, useMapContext, useSelectedId } from 'shared-ui/map'
import { Geo } from 'utils'

import type { FormValues } from './formtypes'
import { edgeKey, withOverride } from './overrideKeys'
import {
  HeadlandCountInput,
  InspectStates,
  OverrideRow,
  useCorridorBankEdgeIds,
  usePassableHolePolygons,
  useViewOnlyFeatures
} from './shared'

export const Headlands = ({
  form,
  inspectQuery
}: {
  form: UseFormReturn<FormValues>
  inspectQuery: HelmInspectQuery
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()

  const selectedEdgeId = useSelectedId()

  const globalCount = form.watch('numberOfHeadlands')

  const edgeHeadlands = useWatch({ control: form.control, name: 'edgeHeadlands' })
  const setCount = (key: string, count: number | null) =>
    form.setValue(
      'edgeHeadlands',
      withOverride(edgeHeadlands, key, count === null ? undefined : { key, count }),
      { shouldDirty: true }
    )

  const edges = useMemo(
    () =>
      (inspectQuery.data?.edges ?? []).map((edge, index) => ({
        ...edge,
        color: planZoneColor(index)
      })),
    [inspectQuery.data?.edges]
  )
  const holes = inspectQuery.data?.holes ?? []
  const corridors = inspectQuery.data?.corridors ?? []

  const passableHolePolygons = usePassableHolePolygons(form, holes)
  const corridorBankEdgeIds = useCorridorBankEdgeIds(form, corridors)
  const hiddenEdgeIds = useMemo(() => {
    const set = new Set<string>(corridorBankEdgeIds)
    for (const edge of edges) {
      if (Geo.featureOverlapsAnyPolygon(edge.feature, passableHolePolygons)) set.add(edge.id)
    }
    return set
  }, [edges, passableHolePolygons, corridorBankEdgeIds])

  const visibleEdges = useMemo(
    () => edges.filter((e) => !hiddenEdgeIds.has(e.id)),
    [edges, hiddenEdgeIds]
  )

  useViewOnlyFeatures('edge', visibleEdges, (edge) => ({ color: edge.color }))

  return (
    <InspectStates
      query={inspectQuery}
      isEmpty={edges.length === 0}
      emptyText={t(($) => $.planning.noRegionsDrawn)}
    >
      <div className="flex flex-col gap-3 py-4">
        <p className="text-sm text-gray-500">{t(($) => $.planning.headlandsInstruction)}</p>
        <div className="flex items-center justify-between">
          <div className="pan-title5">{t(($) => $.planning.headlands)}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {edges.map((edge, edgeIndex) => {
            const key = edgeKey(edge)
            const override = edgeHeadlands.find((o) => o.key === key)
            if (override === undefined) return null
            if (hiddenEdgeIds.has(edge.id)) return null
            const isSelected = edge.id === selectedEdgeId
            return (
              <OverrideRow
                key={edge.id}
                color={edge.color}
                label={t(($) => $.planning.sideLabel, { number: edgeIndex + 1 })}
                isSelected={isSelected}
                onSelect={() => map.drawTool.setSelected(isSelected ? null : edge.id)}
                isOverridden
                onReset={() => setCount(key, null)}
              >
                <HeadlandCountInput
                  count={override.count}
                  globalCount={globalCount}
                  className="mb-0 h-8 w-full rounded-lg border border-gray-200 py-1 text-sm"
                  onChange={(raw) => setCount(key, raw === '' ? null : Number(raw))}
                />
              </OverrideRow>
            )
          })}
        </div>
      </div>
    </InspectStates>
  )
}
